import {QueryParameter} from "../connectivity/query-parameter.interface";
import {ISSUE_TYPE_MILESTONE} from "../constants";
import {FiltersDTO} from "../domain/dtos/filters.dto";
import {Issue} from "../domain/entities/issue.entity";
import {ArrayHelper} from "./array.helper";
import {RteValueHelper} from "./rte-value.helper";
import {belongsToRoot, matchesTypeFilter} from "../domain/helpers/issue-type-root.helper";

export class FilterHelper {

    /**
     * The two reserved activity-filter keys (plan 7.1) — the only two keys this
     * translation does NOT append '.uid' to. Both name a plain scalar/array value the
     * backend compares directly (a symbolic time window, a list of already-resolved
     * uids), not a relation to join into. Comparators are pinned explicitly rather than
     * left to the general rule below: 'in' already falls out of that rule for an array
     * value, but changedWithin is a single string and would otherwise default to 'eq',
     * matching only the exact instant instead of "since".
     */
    private static readonly ACTIVITY_FILTER_COMPARATORS = new Map<string, QueryParameter<any>['comparator']>([
        [`changedBy`, `in`],
        [`changedWithin`, `gte`],
    ]);

    /**
     * Extracts ONLY the two activity-filter keys from a full FiltersDTO — used by both
     * timeline base classes to build their uid-set fetch (7.5), so the extraction logic
     * itself lives in exactly one place, same spirit as asQueryParams being the one
     * place that translates the keys onto the wire (7.1).
     *
     * changedBy is omitted ENTIRELY when unset (E5, "just a window") rather than
     * carried as an explicit `changedBy: undefined` — JSON.stringify would drop such a
     * property anyway on the wire, but keeping it out of the object here means a caller
     * inspecting/testing this method's own result sees the real shape, not an
     * accidental property that happens to serialize away later.
     */
    public static activityFilterOnly(filters: FiltersDTO): FiltersDTO {
        const activityFilter = {} as FiltersDTO;
        if (filters.changedWithin) {
            activityFilter.changedWithin = filters.changedWithin;
        }
        if (filters.changedBy?.length) {
            activityFilter.changedBy = filters.changedBy;
        }
        return activityFilter;
    }

    /**
     * Whether the activity filter restricts anything at all. Its two halves —
     * the person selection and the time window — are INDEPENDENT: either one
     * alone is a filter in its own right, so either one alone makes it active.
     *
     * One definition, called from every place that has to make this decision
     * (both timeline base classes, the task list's empty-result message, and
     * precompileFilters below), because the same condition written out four
     * times is how the previous rule ended up scattered across the codebase.
     *
     * `changedBy?.length` rather than a plain truthiness check: an EMPTY ARRAY
     * IS TRUTHY in JavaScript, and an empty selection is reachable — resolving
     * a filter whose employee no longer exists yields `[]`, which then travels
     * into appState on the next unrelated filter change. Treating that as
     * "active" would produce a filter that restricts nothing, is invisible in
     * the UI, and still triggers a full uid-set fetch.
     */
    public static isActivityFilterActive(filters: FiltersDTO): boolean {
        return !!filters.changedWithin || !!filters.changedBy?.length;
    }

    public static asQueryParams(filters: FiltersDTO): QueryParameter<any>[] {
        const matrix = new Map([
            [`issueStatus`, `status`],
            [`issueType`, `type`],
            [`board`, `project`],
            [`assignees`, `employeeToIssueAssignments.employee`],
        ]);

        return Array.from(Object.entries(filters)).map(([key, value]) => {
            const activityComparator = this.ACTIVITY_FILTER_COMPARATORS.get(key);
            if (activityComparator !== undefined) {
                return <QueryParameter<any>>{key, value, comparator: activityComparator};
            }

            return <QueryParameter<any>>{
                key: `${matrix.get(key) ?? key}.uid`,
                value,
                comparator: Array.isArray(value) ? `in` : `eq`
            }
        })
    }

    /**
     * @param matchedUuids The uid set a prior async fetch resolved for the activity
     *   filter (plan 7.5), or `null` when that fetch has not resolved yet (or the
     *   activity filter is not active at all). The predicate cannot evaluate the
     *   audit-log condition itself — it runs in the browser, which has no audit data —
     *   so it can only check membership in a set the backend already computed.
     *   `null` matches EVERYTHING (fail open): the short window before the first fetch
     *   resolves must not gray out cells based on data that isn't there yet (7.5, point 3).
     */
    public static precompileFilters(filters: FiltersDTO, matchedUuids: Set<string> | null): (issue: Issue) => boolean {
        const precompiledFilters: Array<(issue: Issue) => boolean> = [];
        for (const [key, value] of Object.entries(filters).filter(([key, value]) => key !== `uid` && !!value)) {
            switch (key) {
                case `board`:
                    precompiledFilters.push((issue: Issue) => issue.project?.uid === value);
                    break;
                case `creator`:
                    precompiledFilters.push((issue: Issue) => issue.creator?.uid === value);
                    break;
                case `assignees`:
                    precompiledFilters.push((issue: Issue) => ArrayHelper.intersect(issue.employeeToIssueAssignments.map(a => a.employee!.uid), value).length > 0)
                    break;
                case `issueType`:
                    precompiledFilters.push((issue: Issue) => matchesTypeFilter(issue.type, value as string));
                    break;
                case `keywords`:
                    precompiledFilters.push((issue: Issue) => ArrayHelper.intersect(issue.keywords.map(k => k.uid), value).length > 0);
                    break;
                case `issueStatus`:
                    precompiledFilters.push((issue: Issue) => issue.status !== null && (value as string[]).includes(issue.status.uid));
                    break;
                case `hideTimeRange`:
                    precompiledFilters.push((issue: Issue) => !belongsToRoot(issue.type, ISSUE_TYPE_MILESTONE));
                    break;
                case `searchText`:
                    const searchLower = (value as string).toLowerCase();
                    precompiledFilters.push((issue: Issue) => {
                        const titleMatch = issue.title.toLowerCase().includes(searchLower);
                        const descText = RteValueHelper.plainText(issue.description).toLowerCase();
                        const descMatch = descText.includes(searchLower);
                        return titleMatch || descMatch;
                    });
                    break;
            }
        }

        // Outside the per-key loop on purpose: this predicate reads neither the key
        // nor its value, only the uid set the backend computed. Inside the loop it
        // would have to be attached from two different cases now that either key can
        // stand alone — and would then be attached twice when both are set.
        // matchedUuids null (fetch not resolved yet, or the filter never active) fails
        // open, see this method's own doc comment.
        if (this.isActivityFilterActive(filters)) {
            precompiledFilters.push((issue: Issue) => matchedUuids === null || matchedUuids.has(issue.uid));
        }

        return (issue: Issue) => precompiledFilters.every(fn => fn(issue));
    }
}